import { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, History } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const EDITABLE = ["USD", "TRY", "GBP"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—");

export default function ExchangeRates() {
  const t = useT();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ currency: "USD", rate: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/exchange-rates").then((r) => setData(r.data)).catch(() => {}),
      api.get("/exchange-rates/history").then((r) => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.rate || Number(form.rate) <= 0) { toast.error(t("fx.enterPositive")); return; }
    setBusy(true);
    try {
      await api.post("/exchange-rates", { currency: form.currency, rate: Number(form.rate) });
      toast.success(`${form.currency} ${t("fx.rateEffective")}`);
      setForm({ currency: form.currency, rate: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1000px]">
      <PageHeader title={t("fx.title")} subtitle={t("fx.subtitle")} />
      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div> : (
        <>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="current-rates-card">
              <h3 className="font-heading text-lg font-medium text-slate-900 mb-3 flex items-center gap-2"><TrendingUp className="h-5 w-5" /> {t("fx.currentRates")}</h3>
              <div className="grid grid-cols-2 gap-3">
                {data && Object.entries(data.rates).map(([c, r]) => (
                  <div key={c} className="rounded-lg bg-slate-50 px-3 py-2 flex items-center justify-between" data-testid={`rate-${c}`}>
                    <span className="font-medium text-slate-700">{c}</span>
                    <span className="tabular-nums text-slate-900">{c === "EUR" ? `1.000000 (${t("fx.base")})` : Number(r).toFixed(6)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="set-rate-card">
              <h3 className="font-heading text-lg font-medium text-slate-900 mb-3">{t("fx.setNewRate")}</h3>
              <p className="text-xs text-slate-400 mb-3">{t("fx.setRateHint")}</p>
              <div className="flex gap-2 items-end">
                <div>
                  <label className="text-xs text-slate-500">{t("fx.currency")}</label>
                  <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                    <SelectTrigger className="w-28" data-testid="rate-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{EDITABLE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-500">{t("fx.rateInEur")}</label>
                  <Input type="number" step="0.000001" min="0" value={form.rate} placeholder="0.920000"
                    onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} data-testid="rate-input" />
                </div>
                <Button onClick={save} disabled={busy} className="bg-navy hover:bg-navy-soft text-white" data-testid="rate-save-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="rate-history-card">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <History className="h-4 w-4 text-slate-400" />
              <h3 className="font-heading text-lg font-medium text-slate-900">{t("fx.rateHistory")}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rate-history-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">{t("fx.currency")}</th>
                    <th className="px-3 py-2 font-medium">{t("fx.rate")}</th>
                    <th className="px-3 py-2 font-medium">{t("fx.effectiveFrom")}</th>
                    <th className="px-3 py-2 font-medium">{t("fx.setBy")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">{t("fx.noHistory")}</td></tr>}
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-800">{h.currency}</td>
                      <td className="px-3 py-2 tabular-nums">{Number(h.rate).toFixed(6)}</td>
                      <td className="px-3 py-2 text-slate-500">{fmt(h.effective_from)}</td>
                      <td className="px-3 py-2 text-slate-500">{h.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
